import React, { useMemo, useState } from "react";

import styled from "styled-components";

import {
    legacyTextToMessageDoc,
    messageDocToPlainText,
    parseMessageDoc,
    stringifyMessageDoc,
} from "components/atoms/message";
import Text from "components/atoms/text/Text";

const PanelBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-height: 0;
    padding: 12px;
`;

const InfoBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px;
    border: solid 1px var(--color-Line);
    border-radius: 4px;
    background-color: var(--color-Base1);
`;

const FilterRow = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px;
    border: solid 1px var(--color-Line);
    border-radius: 4px;
    background-color: var(--color-White);
`;

const FilterButton = styled.button`
    height: 28px;
    padding: 0 10px;
    border: 0;
    border-radius: 3px;
    background-color: ${({ $active }) => ($active ? "var(--color-Key)" : "transparent")};
    color: ${({ $active }) => ($active ? "var(--color-White)" : "var(--color-SubBlack)")};
    font-size: 1.2rem;
    font-weight: 500;
    cursor: pointer;

    &:hover {
        background-color: ${({ $active }) => ($active ? "var(--color-Key)" : "var(--color-ButtonHover4)")};
    }
`;

const ThreadList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ThreadCard = styled.button`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    padding: 10px 12px;
    border: solid 1px ${({ $active }) => ($active ? "var(--color-Key)" : "var(--color-Line)")};
    border-radius: 4px;
    background-color: ${({ $active }) => ($active ? "#f3fbf8" : "var(--color-White)")};
    text-align: left;
    cursor: pointer;

    &:hover {
        background-color: #f7faf9;
    }
`;

const ThreadHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

const ThreadTitle = styled(Text).attrs({
    variant: "body-md-500",
    color: "var(--color-SubBlack)",
})``;

const ThreadState = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})`
    flex: 0 0 auto;
`;

const Meta = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})``;

const Quote = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-SubBlack)",
})`
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
`;

const MessageText = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-SubBlack)",
})`
    display: -webkit-box;
    overflow: hidden;
    line-height: 1.4;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
`;

const ExtraMessageText = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})`
    font-size: 1rem;
`;

const EmptyText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-Grey1)",
})`
    padding: 12px 0;
`;

function formatDateTime(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function getFirstMessage(thread) {
    const messages = thread?.messages || [];
    return messages[0] || null;
}

function getExtraMessageCount(thread) {
    const messages = Array.isArray(thread?.messages) ? thread.messages : [];
    return Math.max(0, messages.length - 1);
}

function normalizeMessageContent(content) {
    try {
        parseMessageDoc(content);
        return content || "";
    } catch (_error) {
        return stringifyMessageDoc(legacyTextToMessageDoc(content || ""));
    }
}

function getMessagePreview(content) {
    try {
        return messageDocToPlainText(content).replace(/\s+/g, " ").trim();
    } catch (_error) {
        return messageDocToPlainText(normalizeMessageContent(content)).replace(/\s+/g, " ").trim();
    }
}

function NoteReviewPanel({ isLoading, onSelectThread, reviewThreads = [], selectedThreadId }) {
    const [filter, setFilter] = useState("open");
    const visibleThreads = useMemo(() => {
        if (filter === "resolved") return reviewThreads.filter((thread) => thread.is_resolved);
        if (filter === "all") return reviewThreads;
        return reviewThreads.filter((thread) => !thread.is_resolved);
    }, [filter, reviewThreads]);

    return (
        <PanelBody>
            <InfoBox>
                <Text variant="body-md-500" color="var(--color-SubBlack)">
                    문서 리뷰
                </Text>
                <Text variant="body-sm-400" color="var(--color-Grey1)">
                    선택한 본문 위치에 의견을 남기고, 해결된 리뷰는 닫아둘 수 있습니다.
                </Text>
            </InfoBox>
            <FilterRow aria-label="리뷰 필터">
                <FilterButton type="button" $active={filter === "open"} onClick={() => setFilter("open")}>
                    열림
                </FilterButton>
                <FilterButton type="button" $active={filter === "resolved"} onClick={() => setFilter("resolved")}>
                    해결됨
                </FilterButton>
                <FilterButton type="button" $active={filter === "all"} onClick={() => setFilter("all")}>
                    전체
                </FilterButton>
            </FilterRow>
            {isLoading ? (
                <EmptyText>리뷰를 불러오는 중입니다.</EmptyText>
            ) : visibleThreads.length ? (
                <ThreadList>
                    {visibleThreads.map((thread) => {
                        const firstMessage = getFirstMessage(thread);
                        const extraMessageCount = getExtraMessageCount(thread);
                        return (
                            <ThreadCard
                                key={thread.id}
                                type="button"
                                $active={thread.id === selectedThreadId}
                                aria-pressed={thread.id === selectedThreadId}
                                onClick={() => onSelectThread?.(thread)}
                            >
                                <ThreadHeader>
                                    <ThreadTitle>#{thread.id}</ThreadTitle>
                                    <ThreadState>{thread.is_resolved ? "해결됨" : "열림"}</ThreadState>
                                </ThreadHeader>
                                <Meta>
                                    {thread.created_by_name || "작성자 없음"} · {formatDateTime(thread.created_at)}
                                </Meta>
                                <Quote>{thread.selected_text || "선택 영역 정보가 없습니다."}</Quote>
                                {firstMessage ? (
                                    <MessageText>{getMessagePreview(firstMessage.content)}</MessageText>
                                ) : null}
                                {extraMessageCount ? (
                                    <ExtraMessageText>추가 메시지 {extraMessageCount}개</ExtraMessageText>
                                ) : null}
                            </ThreadCard>
                        );
                    })}
                </ThreadList>
            ) : (
                <EmptyText>표시할 리뷰가 없습니다.</EmptyText>
            )}
        </PanelBody>
    );
}

export default NoteReviewPanel;
